import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type InfiniteData,
} from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import axiosInstance from "@/lib/axios";
import { alertEndpoint } from "@/config/endpoints";
import { dismissAnnouncement, enqueueAnnouncement, viewAlert } from "../announce/announceAlert";
import { showSilentAlertToast } from "../announce/alertToast";
import type {
  AlertAudience,
  AlertAudiencePreview,
  AlertComposeOptions,
  AlertInboxItem,
  AlertInboxPage,
  AlertMode,
  AlertNewPush,
  AlertSentItem,
  AlertSentPage,
  AlertUpdatedPush,
  AssignmentRequestSummary,
} from "../types";

// ─── Query keys ────────────────────────────────────────────
export const ALERT_INBOX_KEY = ["alerts", "inbox"] as const;
export const ALERT_SENT_KEY = ["alerts", "sent"] as const;
export const ALERT_COMPOSE_KEY = ["alerts", "compose-options"] as const;

const ALERT_STALE_MS = 3 * 60 * 1000;
const PAGE_SIZE = 20;

type InboxData = InfiniteData<AlertInboxPage>;
type SentData = InfiniteData<AlertSentPage>;

// ─── Inbox (the Alert box) ─────────────────────────────────
export function useAlertInbox(enabled = true) {
  return useInfiniteQuery<AlertInboxPage>({
    queryKey: ALERT_INBOX_KEY,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = { limit: String(PAGE_SIZE) };
      if (pageParam) params.cursor = pageParam as string;
      const { data } = await axiosInstance.get(alertEndpoint, { params });
      return data as AlertInboxPage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: ALERT_STALE_MS,
    enabled,
  });
}

export function useAlertInboxData(enabled = true) {
  const query = useAlertInbox(enabled);
  const alerts = query.data?.pages.flatMap((p) => p.data) ?? [];
  const unreadCount = query.data?.pages[0]?.unreadCount ?? 0;
  const pendingAnnouncements = query.data?.pages[0]?.pendingAnnouncements ?? [];
  return { ...query, alerts, unreadCount, pendingAnnouncements };
}

// ─── Cache patching ────────────────────────────────────────
function patchInbox(fn: (old: InboxData) => InboxData) {
  queryClient.setQueryData<InboxData>(ALERT_INBOX_KEY, (old) =>
    old ? fn(old) : old,
  );
}

function setUnread(data: InboxData, unreadCount: number): InboxData {
  const safe = Math.max(0, unreadCount);
  return {
    ...data,
    pages: data.pages.map((p, i) => (i === 0 ? { ...p, unreadCount: safe } : p)),
  };
}

function markRows(
  data: InboxData,
  ids: number[] | null,
  readAt: string,
): { data: InboxData; newlyRead: number } {
  let newlyRead = 0;
  const pages = data.pages.map((p) => ({
    ...p,
    data: p.data.map((a) => {
      if (a.readAt) return a;
      if (ids && !ids.includes(a.id)) return a;
      newlyRead += 1;
      return { ...a, readAt };
    }),
    pendingAnnouncements: (p.pendingAnnouncements ?? []).filter(
      (a) => ids != null && !ids.includes(a.id),
    ),
  }));
  return { data: { ...data, pages }, newlyRead };
}

/** Mark alerts read in the cache without a round-trip (the mutations and the
 *  socket echo both land here; idempotent). `null` = every row. */
export function patchAlertsRead(ids: number[] | null, unreadCount: number | null) {
  const readAt = new Date().toISOString();
  patchInbox((old) => {
    const { data, newlyRead } = markRows(old, ids, readAt);
    const current = data.pages[0]?.unreadCount ?? 0;
    return setUnread(
      data,
      unreadCount != null ? unreadCount : ids == null ? 0 : current - newlyRead,
    );
  });
}

/**
 * Socket `alert:new` → prepend to the box, bump the badge, then show it:
 * ANNOUNCE pops the modal, SILENT rings the chat-style corner toast.
 */
export function applyAlertNewPush(push: AlertNewPush | null | undefined) {
  if (!push || typeof push !== "object") return;
  const id = Number(push.alertId ?? push.id);
  if (!Number.isFinite(id) || id <= 0) return;
  const item: AlertInboxItem = {
    id,
    title: String(push.title ?? ""),
    message: String(push.message ?? ""),
    mode: push.mode === "ANNOUNCE" ? "ANNOUNCE" : "SILENT",
    createdAt: push.createdAt || push.timestamp || new Date().toISOString(),
    readAt: null,
    createdBy: push.createdBy ?? { id: 0, name: null },
    request: push.request ?? null,
  };

  const cached = queryClient.getQueryData<InboxData>(ALERT_INBOX_KEY);
  if (cached) {
    patchInbox((old) => {
      if (old.pages.some((p) => p.data.some((a) => a.id === item.id))) return old;
      const [first, ...rest] = old.pages;
      if (!first) return old;
      return setUnread(
        { ...old, pages: [{ ...first, data: [item, ...first.data] }, ...rest] },
        (first.unreadCount ?? 0) + 1,
      );
    });
  } else {
    queryClient.invalidateQueries({ queryKey: ALERT_INBOX_KEY });
  }

  if (item.mode === "ANNOUNCE") {
    enqueueAnnouncement(item);
  } else {
    showSilentAlertToast(item, () => {
      markAlertReadNow(item.id);
      viewAlert(item);
    });
  }
}

/**
 * The assignment request an alert carries changed (closed, usually): patch
 * it onto the row in the box and the Sent tab, and stop its popup. Done /
 * assigned / cancelled all mark the recipients' rows read on the server, so
 * the row reads as read here too.
 */
export function patchAlertRequest(alertId: number | null, request: AssignmentRequestSummary) {
  if (alertId != null) {
    patchInbox((old) => ({
      ...old,
      pages: old.pages.map((p) => ({
        ...p,
        data: p.data.map((a) => (a.id === alertId ? { ...a, request } : a)),
        pendingAnnouncements: (p.pendingAnnouncements ?? []).filter((a) => a.id !== alertId),
      })),
    }));
    queryClient.setQueryData<SentData>(ALERT_SENT_KEY, (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              data: p.data.map((a) => (a.id === alertId ? { ...a, request } : a)),
            })),
          }
        : old,
    );
    if (request.status !== "PENDING") {
      patchAlertsRead([alertId], null);
      dismissAnnouncement(alertId);
    }
  }
  // The lead drawer's "requested" chip reads its own list.
  queryClient.invalidateQueries({ queryKey: ["assignment-requests"] });
}

/** Socket `alert:updated` → read echo from another tab, a sender's removal, or a request closing. */
export function applyAlertUpdatedPush(push: AlertUpdatedPush | null | undefined) {
  if (!push || typeof push !== "object") return;
  switch (push.action) {
    case "read":
      if (push.alertId != null) patchAlertsRead([push.alertId], push.unreadCount ?? null);
      return;
    case "read_all":
      patchAlertsRead(null, 0);
      return;
    case "request_resolved":
      if (push.request) patchAlertRequest(push.alertId ?? null, push.request);
      return;
    case "removed": {
      const id = push.alertId;
      if (id == null) return;
      patchInbox((old) => {
        let droppedUnread = 0;
        const pages = old.pages.map((p) => ({
          ...p,
          data: p.data.filter((a) => {
            if (a.id !== id) return true;
            if (!a.readAt) droppedUnread += 1;
            return false;
          }),
          pendingAnnouncements: (p.pendingAnnouncements ?? []).filter((a) => a.id !== id),
        }));
        const next = { ...old, pages };
        return setUnread(next, (next.pages[0]?.unreadCount ?? 0) - droppedUnread);
      });
      queryClient.invalidateQueries({ queryKey: ALERT_SENT_KEY });
      return;
    }
    default:
      return;
  }
}

// ─── Read mutations ────────────────────────────────────────
/**
 * Mark one alert read from outside a component (the silent toast's click):
 * the box row flips at once, the server's count lands when it answers.
 */
export function markAlertReadNow(id: number) {
  patchAlertsRead([id], null);
  axiosInstance
    .patch(`${alertEndpoint}/${id}/read`)
    .then(({ data }) => patchAlertsRead([id], (data as { unreadCount?: number })?.unreadCount ?? null))
    .catch(() => {});
}

export function useMarkAlertRead() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.patch(`${alertEndpoint}/${id}/read`);
      return data as { unreadCount?: number };
    },
    onMutate: (id) => patchAlertsRead([id], null),
    onSuccess: (data, id) => patchAlertsRead([id], data?.unreadCount ?? null),
  });
}

export function useMarkAllAlertsRead() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.patch(`${alertEndpoint}/read-all`);
      return data as { unreadCount?: number };
    },
    onMutate: () => patchAlertsRead(null, 0),
    onSuccess: () => patchAlertsRead(null, 0),
  });
}

// ─── Compose ───────────────────────────────────────────────
export function useAlertComposeOptions(enabled: boolean) {
  return useQuery<AlertComposeOptions>({
    queryKey: ALERT_COMPOSE_KEY,
    queryFn: async () => {
      const { data } = await axiosInstance.get(`${alertEndpoint}/compose-options`);
      return {
        canCreate: Boolean(data?.canCreate),
        scope: data?.scope === "everyone" ? "everyone" : "below",
        roles: Array.isArray(data?.roles) ? data.roles : [],
        branches: Array.isArray(data?.branches) ? data.branches : [],
        fields: Array.isArray(data?.fields) ? data.fields : [],
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useAlertAudiencePreview(audience: AlertAudience, enabled: boolean) {
  return useQuery<AlertAudiencePreview>({
    queryKey: [
      "alerts",
      "preview",
      [...audience.roleIds].sort((a, b) => a - b).join(","),
      [...audience.branchIds].sort((a, b) => a - b).join(","),
      [...audience.cfItemIds].sort((a, b) => a - b).join(","),
    ],
    queryFn: async () => {
      const { data } = await axiosInstance.post(`${alertEndpoint}/preview`, audience);
      return {
        count: Number(data?.count ?? 0),
        sample: Array.isArray(data?.sample) ? data.sample : [],
        summary: String(data?.summary ?? ""),
      };
    },
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
    enabled,
  });
}

export interface CreateAlertInput extends AlertAudience {
  title: string;
  message: string;
  mode: AlertMode;
}

export function useCreateAlert() {
  return useMutation({
    mutationFn: async (input: CreateAlertInput) => {
      const { data } = await axiosInstance.post(alertEndpoint, input);
      return data?.alert as AlertSentItem;
    },
    onSuccess: (alert) => {
      if (alert?.id) {
        queryClient.setQueryData<SentData>(ALERT_SENT_KEY, (old) => {
          if (!old) return old;
          const [first, ...rest] = old.pages;
          if (!first) return old;
          return { ...old, pages: [{ ...first, data: [alert, ...first.data] }, ...rest] };
        });
      }
      queryClient.invalidateQueries({ queryKey: ALERT_SENT_KEY });
    },
  });
}

// ─── Sent ──────────────────────────────────────────────────
export function useSentAlerts(enabled: boolean) {
  const query = useInfiniteQuery<AlertSentPage>({
    queryKey: ALERT_SENT_KEY,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = { limit: String(PAGE_SIZE) };
      if (pageParam) params.cursor = pageParam as string;
      const { data } = await axiosInstance.get(`${alertEndpoint}/sent`, { params });
      return data as AlertSentPage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: ALERT_STALE_MS,
    enabled,
  });
  const sent = query.data?.pages.flatMap((p) => p.data) ?? [];
  return { ...query, sent };
}

export function useDeleteAlert() {
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${alertEndpoint}/${id}`);
      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData<SentData>(ALERT_SENT_KEY, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({
                ...p,
                data: p.data.filter((a) => a.id !== id),
              })),
            }
          : old,
      );
    },
  });
}
